import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ProgressSpinner } from 'primereact/progressspinner';
import { Checkbox } from 'primereact/checkbox';

import { useScrollLoadingContext } from '../../Contexts/ScrollLoadingContext';
import { useAddCampaignContent } from '../../../queries/campaignContent';
import { setDisableBtn } from '../../../redux/Slices/Global/globalSlice';
import ScrollContainer from '../ScrollContainer';
import SearchInput from './SearchInput';
import MediaStack from './MediaStack';
import PopupScrollContainer from '../PopupScrollContainer';
import TagSelector from '../../Media/components/MediaLayout/TagSelector';
import { setLastElement, setParams } from '../../../redux/Slices/popupScrollSlice';
import PopupScrollSearchInput from './PopupScrollSearchInput';
import { Tooltip } from 'primereact/tooltip';
import { Message } from 'primereact/message';
import { Button } from '../../../components/ui/Button';
import { useGetCompanyTags } from '../../../queries';
import { tagDisplayName } from '../../../utils/helpers';

function PlayLists({ builderRow = false }) {
    const { t } = useTranslation();
    const addCampaignContent = useAddCampaignContent();

    const { isFetching, data, params } = useSelector((state) => state.popupScroll);
    const { layer, assign, companyTags } = useSelector((state) => state.global);
    const screenDetails = useSelector((state) => state.layout.screenDetails);
    const { ids, setIds } = useScrollLoadingContext();
    const [selectedTags, setSelectedTags] = useState([]);

    const lastElementRef = useRef(null);
    const dispatch = useDispatch();
    const { id } = useParams();

    const [searchTerm, setSearchTerm] = useState('');
    let _params = {
        per_page: 10,
    };
    useEffect(() => {
        if (lastElementRef.current) {
            dispatch(setLastElement(lastElementRef.current));
        }
    }, [lastElementRef.current]);

    useEffect(() => {
        return () => setIds([])
    }, [])
    useGetCompanyTags();

    const onPlaylistChange = (e) => {
        let selectedPlaylists = [...ids];

        if (e.checked)
            selectedPlaylists.push(e.value);
        else
            selectedPlaylists.splice(selectedPlaylists.indexOf(e.value), 1);

        setIds(selectedPlaylists);
    }

    const assignHandler = async () => {
        await addCampaignContent.mutateAsync({
            "ids_list": ids,
            "campaign_id": window.location.pathname.includes("layout") ? screenDetails.id : id,
            "layer": layer,
            "type": "playlist",
        })
    }

    useEffect(() => {
        if (assign) {
            dispatch(setDisableBtn(true));
            assignHandler();
        }
    }, [assign])


    const handleRemove = (tag) => {
        const updated = selectedTags.filter(t => t.id !== tag.id);
        setSelectedTags(updated);
        dispatch(setParams({ ...params, tags: ids }))
    };

    // ── Campaign builder add-content sheet — .pp-cb-src card grid, ported
    // pixel-for-pixel from the prototype. Same data/selection/assign as
    // the default render below (ids/onPlaylistChange/assignHandler) —
    // purely a different DOM shape, multi-select kept (tap toggles, the
    // sheet's own footer commits).
    if (builderRow) {
        // ⚠️ PopupScrollContainer must wrap the WHOLE conditional, not just
        // the "has data" branch — it's what actually fires the /playlist/
        // company fetch on mount (same as the default render below). This
        // tab previously only rendered PopupScrollContainer once data was
        // already non-empty, so on a fresh mount (data always starts
        // empty) it never mounted, the fetch never fired, and the tab was
        // permanently stuck on the empty message regardless of real data.
        //
        // ⚠️ Infinite scroll also needs `ref={isLast ? lastElementRef : ...}`
        // on the actual last rendered card — without it the
        // IntersectionObserver in PopupScrollContainer never has anything
        // to observe, so pageNum never advances past 1 no matter how many
        // more playlists exist server-side (this grid was missing it,
        // unlike the default render below / MediaList's / LinksList's
        // builder grids, which all attach it correctly). Combined with a
        // backend bug in `companyPlaylist()` (fixed separately — total_pages
        // was computed off an early-exited counter and always equalled the
        // current page), the two together capped this tab at page 1's 10
        // items regardless of the account's real total.
        return (
            <div className="d-flex flex-column" style={{ minHeight: '30vh' }}>
                <div className="pp-cb-src-toolbar">
                    <div className="pp-cb-src-search">
                        <PopupScrollSearchInput isDisabled={false} />
                    </div>
                    <div className="pp-cb-src-tagfilter">
                        <TagSelector
                            options={companyTags}
                            selectedTags={selectedTags}
                            setSelectedTags={setSelectedTags}
                            placeholder={t('addcontent.filter_by_tags')}
                            handleTagChange={(tagIds) => dispatch(setParams({ ...params, tags: tagIds }))}
                        />
                    </div>
                </div>
                <PopupScrollContainer path={`/playlist/company`} showLoading={false} optionalParam={_params}>
                    {data?.length === 0 && !isFetching ? (
                        <div className="py-5 text-center w-100 opacity-70">
                            {isEmpty(params?.search) && isEmpty(params?.tags) ? t('addcontent.no_playlists_yet') : t('messages.no_results')}
                        </div>
                    ) : (
                        <div className="pp-cb-src-grid">
                            {data?.map((item, index) => {
                                const selected = ids.includes(`${item.id}`);
                                const isLast = index === data.length - 1;
                                return (
                                    <button
                                        type="button"
                                        key={item.id}
                                        ref={isLast ? lastElementRef : null}
                                        className="pp-cb-src"
                                        style={selected ? { borderColor: 'var(--pp-btn-honey)', boxShadow: '0 0 0 1.5px var(--pp-btn-honey) inset' } : undefined}
                                        onClick={() => onPlaylistChange({ checked: !selected, value: item.id })}
                                    >
                                        <span className="si"><i className="bx bxs-playlist" /></span>
                                        <span className="st">{item.name}</span>
                                        <span className="ss">
                                            {t('dragdrop.playlist_label')} · {item.media?.length ?? 0}
                                        </span>
                                        {Array.isArray(item?.tags_list) && item.tags_list.length > 0 && (
                                            <span className="pp-cb-src-tags">
                                                {item.tags_list.map((tag) => (
                                                    <span key={tag.id} className="pp-cb-tagchip">{tagDisplayName(tag, t)}</span>
                                                ))}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                            {isFetching && data?.length ? (
                                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '12px 0' }}>
                                    <ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="3" />
                                </div>
                            ) : null}
                        </div>
                    )}
                </PopupScrollContainer>
                <div className="pp-cb-hint">
                    <i className="pi pi-info-circle" />
                    <span>{t('addcontent.playlist_reuse_hint')}</span>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* CONTROLS */}
            <div className="w-full px-3 py-2 grid gap-2 items-center
  grid-cols-1 sm:grid-cols-[1fr_auto] md:grid-cols-[1fr_minmax(220px,280px)_auto]">

                {/* Search */}
                <div className="min-w-0">
                    <PopupScrollSearchInput isDisabled={data?.length === 0} />
                </div>

                {/* Tags */}
                <div className="min-w-0 sm:justify-self-end md:justify-self-auto">
                    <TagSelector
                        options={companyTags}
                        selectedTags={selectedTags}
                        setSelectedTags={setSelectedTags}
                        placeholder={t('addcontent.filter_by_tags')}
                        handleTagChange={(ids) => dispatch(setParams({ ...params, tags: ids }))}
                    />
                </div>

                {/* Clear */}
                <div className="justify-self-end">
                    <Button
                        label={t('addcontent.clear_all')}
                        icon="pi pi-times"
                        className="py-2 px-2 !border-[#aaa] !text-[#aaa] p-button-secondary p-button-outlined"
                        onClick={() => {
                            dispatch(setParams({ ...params, search: "", tags: [] }));
                            setSelectedTags([]);
                        }}
                    />
                </div>
            </div>

            {/* Selected tag chips */}
            {selectedTags.length > 0 && (
                <div className="mx-3 mb-3 w-full flex gap-1 overflow-x-auto no-scrollbar">
                    {selectedTags.map((tag) => (
                        <small
                            key={tag.id}
                            className="border rounded-full px-2 text-[13px] cursor-pointer whitespace-nowrap"
                            onClick={() => {
                                const updated = selectedTags.filter(t => t.id !== tag.id);
                                setSelectedTags(updated);
                                dispatch(setParams({ ...params, tags: updated.map(t => t.id) }));
                            }}
                        >
                            {(() => { const n = tagDisplayName(tag, t); return n.length > 15 ? n.slice(0, 10) + "..." : n; })()}
                        </small>
                    ))}
                </div>
            )}
            <hr className="w-full mt-0" />

            {/* LIST */}
            <div className="scroll_container scroll_div w-full">
                <PopupScrollContainer path={`/playlist/company`} showLoading={false} optionalParam={_params}>
                    {data?.length === 0 && !isFetching ? (
                        <div className="d-flex p-5 justify-content-center">
                            <Message
                                className="fw-bold fs-3 p-4 w-100"
                                severity="warn"
                                text={isEmpty(params?.search) ? t('addcontent.no_playlists_yet') : t('messages.no_results')}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col px-2 md:px-3">
                            {data?.map((item, index) => {
                                const isLast = index === data?.length - 1;
                                return (
                                    <div
                                        key={`${item.type}-${index}`}
                                        ref={isLast ? lastElementRef : null}
                                        className={`${ids.includes(`${item.id}`) ? "bg-gray-200" : ""} ${index + 1 !== data?.length ? "border-bottom border-gray" : ""
                                            } py-2 px-3`}
                                    >
                                        {/* Row grid - responsive for mobile */}
                                        <label htmlFor={item.id}
                                            className="grid items-center gap-2 w-full cursor-pointer playlist-row-grid"
                                        >
                                            {/* Icon */}
                                            <div className="flex justify-center items-center">
                                                <div className="me-2 p-3 rounded-2 bg-secondary d-flex align-items-center justify-content-center">
                                                    <i className="fas fa-list-ul text-white" />
                                                </div>
                                            </div>

                                            {/* Name + meta (shrinks) */}
                                            <div className="min-w-0">
                                                <div className="truncate">{item.name}</div>

                                                {/* has_unpublished_changes, not item.status — status is pinned to
                                                    INACTIVE forever by PlaylistMediaObserver, so this pill never
                                                    cleared. See PlaylistController::companyPlaylist(). */}
                                                {item.has_unpublished_changes === true && (
                                                    <div className="gap-1 text-muted text-[12px] flex items-center">
                                                        <i
                                                            className="draftedItem cursor-default fa-solid fa-circle-exclamation"
                                                            data-pr-position="bottom"
                                                            data-pr-tooltip="Changes has been made. Please Publish to display the latest update"
                                                        />
                                                        <div
                                                            className="draftedItem cursor-default"
                                                            data-pr-position="bottom"
                                                            data-pr-tooltip="Changes has been made. Please Publish to display the latest update"
                                                        >
                                                            {t('dragdrop.publish_required')}
                                                        </div>
                                                    </div>
                                                )}

                                                {Array.isArray(item?.tags_list) && item.tags_list.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {item.tags_list.map((tag, i) => (
                                                            <div
                                                                key={i}
                                                                className="flex items-center bg-gray-50 border px-2 rounded-full cursor-default"
                                                            >
                                                                <i className="pi pi-tag text-[13px] pr-1 text-gray-500" />
                                                                <small className="truncate">{tagDisplayName(tag, t)}</small>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                <Tooltip target=".draftedItem" showDelay={100} className="fs-8" />
                                            </div>

                                            {/* Media stack (md+) */}
                                            <div className="hidden md:flex min-w-0">
                                                {item.media?.length ? <MediaStack mediaArray={item.media} /> : t('addcontent.empty_playlist')}
                                            </div>

                                            {/* Checkbox */}
                                            <div className="justify-self-end pr-2">
                                                <Checkbox
                                                    inputId={item.id}
                                                    name="media"
                                                    className="me-1"
                                                    value={item.id}
                                                    onChange={onPlaylistChange}
                                                    checked={ids.includes(`${item.id}`)}
                                                />
                                            </div>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </PopupScrollContainer>
            </div>

        </>

    )

}

export default PlayLists